import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Tooltip,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ArrowBack,
  ExpandMore,
  ChevronRight,
  ChevronLeft
} from '@mui/icons-material';

import { getIRRComparisonData, getGroundTruth, saveGroundTruth } from '../../service/labelingService';

// Old rrweb replay imports
import ReplayWorkspacePage from '../workspaceReplay/ReplayWorkspacePage';
import { convertToReplayFormat } from './PlayerView/replayDataConverter';
import { getRrwebEventsBySessionAndUser } from '../../service/rrwebService';
import { convertSnapshotsJsonToReplayFormat } from './PlayerView/snapshotJsonConverter';
import { getCodesBySessionAndUser } from '../../service/codeService';

// Telemetry replay imports
import { getTelemetryEventsBySessionAndUser } from '../workspace/service/telemetryService';
import ReplayWorkspace from '../workspaceReplay2/ReplayWorkspace';
import ReplayControls from '../workspaceReplay2/components/ReplayControls';
import { usePlayback } from '../workspaceReplay2/hooks/usePlayback';

// Auto-segmentation
import { autoSegmentEvents } from '../workspaceReplay2/utils/autoSegmentation';

// Import snapshot data for rrweb sessions
import session_11155 from './sessions/session_11155.json';
import session_11173 from './sessions/session_11173.json';
import session_11197 from './sessions/session_11197.json';
import session_11201 from './sessions/session_11201.json';
import session_11217 from './sessions/session_11217.json';
import session_11225 from './sessions/session_11225.json';

const snapshotsMap = {
  11155: session_11155,
  11173: session_11173,
  11197: session_11197,
  11201: session_11201,
  11217: session_11217,
  11225: session_11225,
};

const SESSION_CONFIG = {
  11357: { playerType: 'telemetry', vispiSessionId: 643 },
  11361: { playerType: 'telemetry', vispiSessionId: 643 },
  11385: { playerType: 'telemetry', vispiSessionId: 643 },
  11435: { playerType: 'telemetry', vispiSessionId: 643 },
  11357: { playerType: 'telemetry', vispiSessionId: 643 },
  11361: { playerType: 'telemetry', vispiSessionId: 643 },
  11385: { playerType: 'telemetry', vispiSessionId: 643 },
  11435: { playerType: 'telemetry', vispiSessionId: 643 },
  11556: { playerType: 'telemetry', vispiSessionId: 654 },
  11588: { playerType: 'telemetry', vispiSessionId: 654 },
  11498: { playerType: 'telemetry', vispiSessionId: 654 },
  11714: { playerType: 'telemetry', vispiSessionId: 654 },
  11718: { playerType: 'telemetry', vispiSessionId: 654 },
};

const SESSION_DATA = {
  643: {
    task: "Create a class named Rectangle that takes two parameters in its constructor: width and height. Include a method named get_area that returns the area of the rectangle (width * height).",
  },
  658: {
    task: "Create a class named Rectangle that takes two parameters in its constructor: width and height.\n\n1.) Include a method named get_area that returns the area (width * height)\n\n2.) Include a __str__ method so that printing a Rectangle displays: Rectangle(4x5, area=20)",
  },
  659: {
    task: "Write a function called is_palindrome that takes a string and returns True if the string reads the same forwards and backwards, and False otherwise. Ignore uppercase and lowercase differences.",
},
};

const HARDCODED_SESSION = {
  id: 643,
  subject: "OOP: Rectangle Class",
  task: "Create a class named Rectangle that takes two parameters in its constructor: width and height. Include a method named get_area that returns the area of the rectangle (width * height).",
  test_code: {
    starter: "# Variable Setup for Unit Tests\nglobal parameter_values\nparameter_values = ['4, 5', '6, 3', '5, 3']\nglobal return_values\nreturn_values = [20, 18, 15]\nglobal result\nresult = []",
    template: "",
    unit_test: "# Execute tests\nfor i, input_expr in enumerate(parameter_values):\n    try:\n        width, height = map(int, input_expr.split(', '))\n        rect = Rectangle(width, height)\n        if rect.get_area() == return_values[i]:\n            result.append(True)\n        else:\n            result.append(False)\n    except:\n        result.append(False)"
  },
  test_list: [
    { name: "Test Case 1", input: "4, 5", expected: "20" },
    { name: "Test Case 2", input: "6, 3", expected: "18" },
    { name: "Test Case 3", input: "5, 3", expected: "15" }
  ]
};

const LABELER_COLORS = {
  'David': '#3b82f6',
  'Susan': '#10b981',
  'Minsun': '#f59e0b',
  'Xiaohang': '#ef4444',
};

const LABELER_ORDER = ['Minsun', 'Xiaohang', 'Rezky', 'David'];

const sortLabelers = (labelers) => {
  return [...labelers].sort((a, b) => {
    const aIndex = LABELER_ORDER.indexOf(a.labelerName);
    const bIndex = LABELER_ORDER.indexOf(b.labelerName);
    const aOrder = aIndex === -1 ? 999 : aIndex;
    const bOrder = bIndex === -1 ? 999 : bIndex;
    return aOrder - bOrder;
  });
};

const NUDGE_AMOUNT = 500;
const NUDGE_AMOUNT_SHIFT = 2000;

export default function IRRComparisonView({ 
  session,
  onBack 
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  
  const [replayEvents, setReplayEvents] = useState([]);
  const [replayMetadata, setReplayMetadata] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  
  const [telemetryEvents, setTelemetryEvents] = useState([]);
  
  const [activeTab, setActiveTab] = useState(0);

  const [slicePoints, setSlicePoints] = useState([]);
  const [groundTruthLoaded, setGroundTruthLoaded] = useState(false);
  const [selectedSlice, setSelectedSlice] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const groundTruthBarRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  const containerRef = useRef(null);

  const [labelsExpanded, setLabelsExpanded] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [autoSegExpanded, setAutoSegExpanded] = useState(false);

  const [autoSegments, setAutoSegments] = useState([]);

  const sessionData = SESSION_DATA[session.vispiSessionId] || SESSION_DATA[643];

  const playerType = SESSION_CONFIG[session.studentUserId]?.playerType 
    || (session.vispiSessionId >= 654 ? 'telemetry' : 'rrweb');
  const isTelemetry = playerType === 'telemetry';

  const { telemetryStartTime, telemetryDuration, filteredTelemetryEvents } = useMemo(() => {
    if (!isTelemetry || telemetryEvents.length === 0) {
      return { telemetryStartTime: 0, telemetryDuration: 0, filteredTelemetryEvents: [] };
    }

    const firstEvent = telemetryEvents[0];
    const start = firstEvent.timestamp;

    const allPassedIndex = telemetryEvents.findIndex(e => {
      if (e.type !== 'TEST_CASE_RESULT') return false;
      const { passed_count, total_tests } = e.payload || {};
      return passed_count === total_tests && total_tests > 0;
    });

    const filtered = allPassedIndex !== -1 
      ? telemetryEvents.slice(0, allPassedIndex + 1)
      : telemetryEvents;

    const lastEvent = filtered[filtered.length - 1];
    const end = lastEvent.timestamp;

    return {
      telemetryStartTime: start,
      telemetryDuration: end - start,
      filteredTelemetryEvents: filtered
    };
  }, [telemetryEvents, isTelemetry]);

  const playback = usePlayback(telemetryDuration);

  const sessionDuration = isTelemetry 
    ? (telemetryDuration || 1) 
    : (replayMetadata?.duration || 1);

  // Extract TERMINAL_RUN timestamps for emoji markers
  const terminalRunMarkers = useMemo(() => {
    if (!isTelemetry || filteredTelemetryEvents.length === 0) return [];
    return filteredTelemetryEvents
      .filter(e => e.type === 'TERMINAL_RUN')
      .map(e => e.timestamp - telemetryStartTime);
  }, [filteredTelemetryEvents, telemetryStartTime, isTelemetry]);

  useEffect(() => {
    if (isTelemetry) {
      setCurrentTime(playback.currentTime);
    }
  }, [playback.currentTime, isTelemetry]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await getIRRComparisonData(
          session.vispiSessionId,
          session.studentUserId
        );
        
        console.log('IRR Comparison Data:', data);
        setComparisonData(data);

        try {
          const gt = await getGroundTruth(session.vispiSessionId, session.studentUserId);
          if (gt.slicePoints && gt.slicePoints.length > 0) {
            setSlicePoints(gt.slicePoints);
          }
        } catch (err) {
          console.warn('Could not load ground truth:', err);
        }
        setGroundTruthLoaded(true);

        const vispiSessionId = session.vispiSessionId;
        const studentUserId = session.studentUserId;

        if (isTelemetry) {
          const events = await getTelemetryEventsBySessionAndUser(vispiSessionId, studentUserId);
          
          if (events && events.length > 0) {
            events.sort((a, b) => a.timestamp - b.timestamp);
            setTelemetryEvents(events);
          }
        } else {
          let rrwebEvents = [];
          try {
            rrwebEvents = await getRrwebEventsBySessionAndUser(vispiSessionId, studentUserId);
            rrwebEvents = rrwebEvents || [];
          } catch (err) {
            console.warn('Could not fetch rrweb events:', err);
          }

          let codeSnapshots = [];
          try {
            codeSnapshots = await getCodesBySessionAndUser(vispiSessionId, studentUserId);
            codeSnapshots = codeSnapshots || [];
          } catch (err) {
            console.warn('Could not fetch code snapshots:', err);
          }

          let { events, metadata } = convertToReplayFormat(rrwebEvents, codeSnapshots);

          const snapshotsJson = snapshotsMap[studentUserId];
          if (snapshotsJson) {
            const { events: snapshotEvents } = convertSnapshotsJsonToReplayFormat(snapshotsJson);
            events = [...events, ...snapshotEvents].sort((a, b) => a.timestamp - b.timestamp);
            
            if (events.length > 0) {
              const timestamps = events.map(e => e.timestamp);
              metadata = {
                ...metadata,
                sessionStartTime: Math.min(...timestamps),
                sessionEndTime: Math.max(...timestamps),
                duration: Math.max(...timestamps) - Math.min(...timestamps),
                totalEvents: events.length
              };
            }
          }

          const maxDurationOverrides = { 11225: 509000 };
          
          if (maxDurationOverrides[studentUserId] && events.length > 0) {
            const maxDuration = maxDurationOverrides[studentUserId];
            const startTime = events[0].timestamp;
            const cutoffTime = startTime + maxDuration;
            
            events = events.filter(e => e.timestamp <= cutoffTime);
            
            if (events.length > 0) {
              const timestamps = events.map(e => e.timestamp);
              metadata = {
                ...metadata,
                sessionEndTime: Math.max(...timestamps),
                duration: Math.max(...timestamps) - Math.min(...timestamps),
                totalEvents: events.length
              };
            }
          }

          setReplayEvents(events);
          setReplayMetadata(metadata);
        }

      } catch (err) {
        console.error('Error loading IRR data:', err);
        setError('Failed to load comparison data: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [session, isTelemetry]);

  useEffect(() => {
    if (isTelemetry && filteredTelemetryEvents.length > 0 && telemetryDuration > 0) {
      const segments = autoSegmentEvents(filteredTelemetryEvents, telemetryStartTime, telemetryDuration);
      setAutoSegments(segments);
      console.log('Auto-segments generated:', segments.length);
    }
  }, [filteredTelemetryEvents, telemetryStartTime, telemetryDuration, isTelemetry]);

  useEffect(() => {
    if (!groundTruthLoaded) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await saveGroundTruth(
          session.vispiSessionId,
          session.studentUserId,
          slicePoints
        );
        console.log('Ground truth saved:', slicePoints.length, 'slice points');
      } catch (err) {
        console.error('Error saving ground truth:', err);
      }
    }, 1000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [slicePoints, groundTruthLoaded, session.vispiSessionId, session.studentUserId]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedSlice === null) return;

      const sliceIndex = slicePoints.indexOf(selectedSlice);
      if (sliceIndex === -1) return;

      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        const nudge = e.shiftKey ? NUDGE_AMOUNT_SHIFT : NUDGE_AMOUNT;
        const direction = e.key === 'ArrowLeft' ? -1 : 1;
        const newTime = selectedSlice + (direction * nudge);

        const MIN_GAP = 200;
        if (newTime <= MIN_GAP || newTime >= sessionDuration - MIN_GAP) return;

        const otherSlices = slicePoints.filter((_, i) => i !== sliceIndex);
        const tooClose = otherSlices.some(p => Math.abs(p - newTime) < MIN_GAP);
        if (tooClose) return;

        setSlicePoints(prev => {
          const updated = [...prev];
          updated[sliceIndex] = newTime;
          return updated.sort((a, b) => a - b);
        });
        setSelectedSlice(newTime);

        if (isTelemetry && playback.seek) {
          playback.seek(newTime);
        }
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        setSlicePoints(prev => prev.filter(p => p !== selectedSlice));
        setSelectedSlice(null);
      }

      if (e.key === 'Escape') {
        setSelectedSlice(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedSlice, slicePoints, sessionDuration, isTelemetry, playback]);

  const handleDragStart = useCallback((timestamp, e) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedSlice(timestamp);
    setIsDragging(true);
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      if (!groundTruthBarRef.current || selectedSlice === null) return;

      const rect = groundTruthBarRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
      const ratio = x / rect.width;
      const newTime = Math.round(ratio * sessionDuration);

      const MIN_GAP = 200;
      if (newTime <= MIN_GAP || newTime >= sessionDuration - MIN_GAP) return;

      const sliceIndex = slicePoints.indexOf(selectedSlice);
      if (sliceIndex === -1) return;

      const otherSlices = slicePoints.filter((_, i) => i !== sliceIndex);
      const tooClose = otherSlices.some(p => Math.abs(p - newTime) < MIN_GAP);
      if (tooClose) return;

      setSlicePoints(prev => {
        const updated = [...prev];
        updated[sliceIndex] = newTime;
        return updated.sort((a, b) => a - b);
      });
      setSelectedSlice(newTime);

      if (isTelemetry && playback.seek) {
        playback.seek(newTime);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, selectedSlice, slicePoints, sessionDuration, isTelemetry, playback]);

  const getLabelerColor = (name) => {
    return LABELER_COLORS[name] || '#6b7280';
  };

  const filteredLabelers = useMemo(() => {
    if (!comparisonData?.labelers) return [];
    
    const studentName = session.studentName || '';
    
    return comparisonData.labelers.filter(labeler => {
      const labelerName = labeler.labelerName || '';
      
      if (labelerName.startsWith('Guest_')) {
        return studentName.includes('qzkvho');
      }
      
      return true;
    });
  }, [comparisonData, session.studentName]);

  const sortedLabelers = useMemo(() => {
    if (!filteredLabelers.length) return [];
    const labelersWithTags = filteredLabelers.filter(l => l.tags && l.tags.length > 0);
    return sortLabelers(labelersWithTags);
  }, [filteredLabelers]);

  const behavioralIRR = useMemo(() => {
    if (!sortedLabelers || sortedLabelers.length < 2) return null;
    
    const duration = sessionDuration;
    if (duration <= 0) return null;
    
    const sampleInterval = 1000;
    const samples = Math.floor(duration / sampleInterval);
    if (samples === 0) return null;
    
    let agreements = 0;
    let total = 0;
    
    for (let i = 0; i < samples; i++) {
      const time = i * sampleInterval;
      
      const behaviorsAtTime = sortedLabelers.map(labeler => {
        const tag = labeler.tags?.find(t => {
          const start = t.startTime ?? t.start_time ?? 0;
          const end = t.endTime ?? t.end_time ?? 0;
          return time >= start && time < end;
        });
        return tag ? (tag.behavior?.id ?? tag.behaviorId ?? tag.behavior_id ?? 'unknown') : null;
      });
      
      for (let j = 0; j < behaviorsAtTime.length; j++) {
        for (let k = j + 1; k < behaviorsAtTime.length; k++) {
          if (behaviorsAtTime[j] !== null && behaviorsAtTime[k] !== null) {
            total++;
            if (behaviorsAtTime[j] === behaviorsAtTime[k]) {
              agreements++;
            }
          } else if (behaviorsAtTime[j] === null && behaviorsAtTime[k] === null) {
            total++;
            agreements++;
          }
        }
      }
    }
    
    if (total === 0) return null;
    return Math.round((agreements / total) * 100);
  }, [sortedLabelers, sessionDuration]);

  const preQueryPatterns = useMemo(() => {
    if (!filteredLabelers.length) return [];
    
    const sorted = sortLabelers(filteredLabelers);
    
    return sorted.map(labeler => {
      const preQueryPattern = labeler.patterns?.find(p => 
        p.isPreQuery === true || p.is_pre_query === true
      );
      
      const normalizedPattern = preQueryPattern ? {
        ...preQueryPattern,
        name: preQueryPattern.name,
        description: preQueryPattern.description,
        startTime: preQueryPattern.startTime ?? preQueryPattern.start_time,
        endTime: preQueryPattern.endTime ?? preQueryPattern.end_time,
        isPreQuery: preQueryPattern.isPreQuery ?? preQueryPattern.is_pre_query,
        assessment: preQueryPattern.assessment ? {
          productivity: preQueryPattern.assessment.productivity,
          interventionRecommendation: preQueryPattern.assessment.interventionRecommendation ?? preQueryPattern.assessment.intervention_recommendation,
          annotatorConfidence: preQueryPattern.assessment.annotatorConfidence ?? preQueryPattern.assessment.annotator_confidence,
          isComplete: preQueryPattern.assessment.isComplete ?? preQueryPattern.assessment.is_complete
        } : null
      } : null;
      
      return {
        labelerName: labeler.labelerName,
        pattern: normalizedPattern
      };
    });
  }, [filteredLabelers]);

  const formatTime = (ms) => {
    const num = Number(ms);
    if (!Number.isFinite(num) || num < 0) return '0:00';
    const seconds = Math.floor(num / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // --- Handlers ---
  const handleGroundTruthClick = useCallback((e) => {
    if (isDragging) return;
    if (!groundTruthBarRef.current) return;

    const rect = groundTruthBarRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = x / rect.width;
    const timestamp = Math.round(ratio * sessionDuration);

    const CLICK_THRESHOLD = sessionDuration * 0.01;
    const nearbySlice = slicePoints.find(p => Math.abs(p - timestamp) < CLICK_THRESHOLD);
    if (nearbySlice !== undefined) {
      setSelectedSlice(nearbySlice);
      if (isTelemetry && playback.seek) {
        playback.seek(nearbySlice);
      }
      return;
    }

    setSelectedSlice(null);

    const MIN_GAP = 200;
    const tooClose = slicePoints.some(p => Math.abs(p - timestamp) < MIN_GAP);
    if (tooClose || timestamp <= MIN_GAP || timestamp >= sessionDuration - MIN_GAP) return;

    setSlicePoints(prev => [...prev, timestamp].sort((a, b) => a - b));
  }, [sessionDuration, slicePoints, isDragging, isTelemetry, playback]);

  const handleCutAtPlayhead = useCallback(() => {
    const MIN_GAP = 200;
    const tooClose = slicePoints.some(p => Math.abs(p - currentTime) < MIN_GAP);
    if (!tooClose && currentTime > MIN_GAP && currentTime < sessionDuration - MIN_GAP) {
      setSlicePoints(prev => [...prev, currentTime].sort((a, b) => a - b));
    }
  }, [currentTime, sessionDuration, slicePoints]);

  const handleDeleteSelected = useCallback(() => {
    if (selectedSlice === null) return;
    setSlicePoints(prev => prev.filter(p => p !== selectedSlice));
    setSelectedSlice(null);
  }, [selectedSlice]);

  const handleSelectSlice = useCallback((timestamp, e) => {
    e.stopPropagation();
    setSelectedSlice(prev => prev === timestamp ? null : timestamp);
    if (isTelemetry && playback.seek) {
      playback.seek(timestamp);
    }
  }, [isTelemetry, playback]);

  const handleExportGroundTruth = useCallback(() => {
    const baseTime = isTelemetry ? telemetryStartTime : 0;
    const eventsSource = isTelemetry ? filteredTelemetryEvents : [];
    const points = [0, ...slicePoints, sessionDuration];

    const segments = points.slice(0, -1).map((start, i) => {
      const end = points[i + 1];
      const segEvents = eventsSource
        .filter(e => {
          const t = e.timestamp - baseTime;
          return t >= start && t < end;
        })
        .map(e => ({
          type: e.type,
          timestamp: e.timestamp,
          relativeTime: e.timestamp - baseTime,
          payload: e.payload,
        }));

      return {
        segmentIndex: i,
        startTime: start,
        endTime: end,
        durationMs: end - start,
        durationFormatted: formatTime(end - start),
        eventCount: segEvents.length,
        events: segEvents,
      };
    });

    const exportData = {
      studentUserId: session.studentUserId,
      studentName: session.studentName,
      vispiSessionId: session.vispiSessionId,
      sessionDuration,
      sessionDurationFormatted: formatTime(sessionDuration),
      totalSlicePoints: slicePoints.length,
      slicePoints,
      totalSegments: segments.length,
      segments,
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ground_truth_${session.studentUserId}_segments.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [slicePoints, sessionDuration, session, isTelemetry, telemetryStartTime, filteredTelemetryEvents]);

  const handleExportAutoSegments = useCallback(() => {
    const baseTime = isTelemetry ? telemetryStartTime : 0;
    const eventsSource = isTelemetry ? filteredTelemetryEvents : [];

    const segments = autoSegments.map((seg, i) => {
      const segEvents = eventsSource
        .filter(e => {
          const t = e.timestamp - baseTime;
          return t >= seg.startTime && t < seg.endTime;
        })
        .map(e => ({
          type: e.type,
          timestamp: e.timestamp,
          relativeTime: e.timestamp - baseTime,
          payload: e.payload,
        }));

      return {
        segmentIndex: i,
        startTime: seg.startTime,
        endTime: seg.endTime,
        durationMs: seg.endTime - seg.startTime,
        durationFormatted: formatTime(seg.endTime - seg.startTime),
        suggestedBehavior: seg.suggestedBehavior?.id || null,
        suggestedBehaviorLabel: seg.suggestedBehavior?.label || null,
        preQueryPause: seg.preQueryPause || null,
        eventCount: segEvents.length,
        events: segEvents,
      };
    });

    const exportData = {
      studentUserId: session.studentUserId,
      studentName: session.studentName,
      vispiSessionId: session.vispiSessionId,
      sessionDuration,
      sessionDurationFormatted: formatTime(sessionDuration),
      totalSegments: segments.length,
      terminalRunCount: terminalRunMarkers.length,
      terminalRunTimestamps: terminalRunMarkers,
      segments,
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `auto_segments_${session.studentUserId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [autoSegments, sessionDuration, session, isTelemetry, telemetryStartTime, filteredTelemetryEvents, terminalRunMarkers]);

  const handleExportLabelerLabels = useCallback(() => {
    const labelerData = sortedLabelers.map(labeler => ({
      labelerName: labeler.labelerName,
      totalTags: labeler.tags?.length || 0,
      tags: (labeler.tags || []).map(tag => ({
        startTime: tag.startTime ?? tag.start_time ?? 0,
        endTime: tag.endTime ?? tag.end_time ?? 0,
        startFormatted: formatTime(tag.startTime ?? tag.start_time ?? 0),
        endFormatted: formatTime(tag.endTime ?? tag.end_time ?? 0),
        durationMs: (tag.endTime ?? tag.end_time ?? 0) - (tag.startTime ?? tag.start_time ?? 0),
        behaviorId: tag.behavior?.id ?? tag.behaviorId ?? tag.behavior_id ?? 'unknown',
        behaviorLabel: tag.behavior?.label ?? tag.behaviorLabel ?? tag.behavior_label ?? 'Unknown',
        confidence: tag.confidence ?? null,
        thinkingSubcategory: tag.thinkingSubcategory ?? tag.thinking_subcategory ?? null,
      }))
    }));

    const exportData = {
      studentUserId: session.studentUserId,
      studentName: session.studentName,
      vispiSessionId: session.vispiSessionId,
      sessionDuration,
      sessionDurationFormatted: formatTime(sessionDuration),
      behavioralIRR,
      totalLabelers: labelerData.length,
      labelers: labelerData,
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `labeler_labels_${session.studentUserId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [sortedLabelers, sessionDuration, session, behavioralIRR]);

  const renderDescriptionTab = () => {
    const patterns = preQueryPatterns;
    
    if (patterns.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4, color: '#9ca3af' }}>
          <Typography variant="body2">No labelers found for comparison</Typography>
        </Box>
      );
    }
    
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {patterns.map((item) => (
          <Accordion
            key={item.labelerName}
            defaultExpanded
            disableGutters
            elevation={0}
            sx={{
              border: '1px solid #e5e7eb',
              borderRadius: '8px !important',
              '&:before': { display: 'none' },
              overflow: 'hidden'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                minHeight: 48,
                borderBottom: `4px solid ${getLabelerColor(item.labelerName)}`,
                '& .MuiAccordionSummary-content': { my: 1 }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight="600" sx={{ color: '#374151' }}>
                  {item.labelerName}
                </Typography>
                {!item.pattern && (
                  <Chip label="No pattern" size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#f3f4f6' }} />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 1.5, pb: 1.5 }}>
              {item.pattern ? (
                <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.8rem', lineHeight: 1.5 }}>
                  {item.pattern.description || 'No description provided'}
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '0.8rem' }}>
                  This labeler did not create a pre-query pattern.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    );
  };

  const renderProductivityTab = () => {
    const patterns = preQueryPatterns;
    
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {patterns.map((item) => {
          const productivity = item.pattern?.assessment?.productivity;
          return (
            <Accordion
              key={item.labelerName}
              defaultExpanded
              disableGutters
              elevation={0}
              sx={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px !important',
                '&:before': { display: 'none' },
                overflow: 'hidden'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  minHeight: 48,
                  borderBottom: `4px solid ${getLabelerColor(item.labelerName)}`,
                  '& .MuiAccordionSummary-content': { my: 1 }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', pr: 1 }}>
                  <Typography variant="body2" fontWeight="600" sx={{ color: '#374151' }}>
                    {item.labelerName}
                  </Typography>
                  {productivity && (
                    <Typography variant="caption" sx={{ color: '#6b7280', ml: 'auto' }}>
                      {['Unproductive', 'Low', 'Neutral', 'Good', 'Excellent'][productivity - 1]}
                    </Typography>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 1.5, pb: 1.5 }}>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {[1, 2, 3, 4, 5].map((v) => (
                    <Box
                      key={v}
                      sx={{
                        flex: 1,
                        height: 36,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: productivity === v ? getLabelerColor(item.labelerName) : '#f3f4f6',
                        color: productivity === v ? '#fff' : '#9ca3af',
                        fontWeight: productivity === v ? 700 : 500,
                        fontSize: '0.85rem',
                        border: productivity === v ? 'none' : '1px solid #e5e7eb'
                      }}
                    >
                      {v}
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    );
  };

  const renderInterventionTab = () => {
    const patterns = preQueryPatterns;
    
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {patterns.map((item) => {
          const intervention = item.pattern?.assessment?.interventionRecommendation;
          const hasContent = !!intervention;
          
          return (
            <Accordion
              key={item.labelerName}
              defaultExpanded
              disableGutters
              elevation={0}
              sx={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px !important',
                '&:before': { display: 'none' },
                overflow: 'hidden'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  minHeight: 48,
                  borderBottom: `4px solid ${getLabelerColor(item.labelerName)}`,
                  '& .MuiAccordionSummary-content': { my: 1 }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight="600" sx={{ color: '#374151' }}>
                    {item.labelerName}
                  </Typography>
                  {!hasContent && (
                    <Chip label="No recommendation" size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#f3f4f6' }} />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 1.5, pb: 1.5 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: hasContent ? '#374151' : '#9ca3af', 
                    fontStyle: hasContent ? 'normal' : 'italic',
                    fontSize: '0.8rem',
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {intervention || 'No intervention recommendation provided'}
                </Typography>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    );
  };

  const renderReplayPlayer = () => {
    if (isTelemetry) {
      if (filteredTelemetryEvents.length === 0) {
        return (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', height: '100%' }}>
            <Typography>No telemetry data available</Typography>
          </Box>
        );
      }

      return (
        <>
          <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
            <div style={{
              width: '133.33%',
              height: '133.33%',
              transform: 'scale(0.75)',
              transformOrigin: 'top left',
            }}>
              <ReplayWorkspace 
                playback={playback} 
                events={filteredTelemetryEvents}
                startTime={telemetryStartTime}
                task={sessionData.task}
                starterCode=""
              />
            </div>
          </Box>

          <ReplayControls
            currentTime={playback.currentTime}
            duration={playback.duration}
            isPlaying={playback.isPlaying}
            playbackSpeed={playback.playbackSpeed}
            onTogglePlay={playback.togglePlay}
            onSeek={playback.seek}
            onReset={playback.reset}
            onCycleSpeed={playback.cycleSpeed}
            taggedBlocks={[]}
            showLabeling={false}
            activeRecording={null}
            events={filteredTelemetryEvents}
            startTime={telemetryStartTime}
          />
        </>
      );
    } else {
      if (replayEvents.length === 0) {
        return (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', height: '100%' }}>
            <Typography>No replay data available</Typography>
          </Box>
        );
      }

      return (
        <ReplayWorkspacePage 
          sessionId={session.vispiSessionId}
          task={sessionData.task}
          testCases={sessionData.test_code}
          testList={sessionData.test_list}
          starterCode=""
          events={replayEvents}
          onTimeUpdate={setCurrentTime}
          taggedBlocks={[]}
          activeRecording={null}
          selectionMode={false}
        />
      );
    }
  };

  if (loading) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#111827' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ color: '#3b82f6' }} />
          <Typography variant="body2" sx={{ mt: 2, color: '#9ca3af' }}>Loading comparison data...</Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#111827' }}>
        <Box sx={{ textAlign: 'center', color: '#9ca3af' }}>
          <Typography variant="body1" sx={{ mb: 2, color: '#f87171' }}>{error}</Typography>
          <Button variant="outlined" onClick={onBack} sx={{ color: '#9ca3af', borderColor: '#374151' }}>
            Go Back
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box ref={containerRef} sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#111827' }} tabIndex={-1}>
      
      {/* Header */}
      <Box sx={{ p: 2, bgcolor: '#111827', borderBottom: '1px solid #374151', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={onBack} sx={{ color: '#9ca3af', '&:hover': { color: '#ffffff', bgcolor: 'rgba(255,255,255,0.1)' } }}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="subtitle1" sx={{ color: '#ffffff', fontWeight: 600 }}>
              IRR Comparison - {session.studentName}
            </Typography>
            <Typography variant="caption" sx={{ color: '#9ca3af' }}>
              {filteredLabelers.length} labelers • Student ID: {session.studentUserId} • {formatTime(sessionDuration)}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        
        {/* Left: Replay with stacked timelines */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: '#1f2937' }}>
          
          {/* Replay Player */}
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {renderReplayPlayer()}
          </Box>
          
          {/* Timelines Section */}
          <Box sx={{ p: 2, bgcolor: '#111827', borderTop: '1px solid #374151' }}>

            {/* Ground Truth Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" fontWeight="600" sx={{ color: '#6b7280', textTransform: 'uppercase' }}>
                Ground Truth
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Chip
                  label={`✂️ Cut at ${formatTime(currentTime)}`}
                  size="small"
                  onClick={handleCutAtPlayhead}
                  sx={{
                    height: 22, fontSize: '0.65rem', fontWeight: 600, cursor: 'pointer',
                    bgcolor: '#374151', color: '#a78bfa', border: '1px solid #4b5563',
                    '&:hover': { bgcolor: '#4b5563' },
                  }}
                />
                {selectedSlice !== null && (
                  <Chip
                    label={`🗑️ Delete (${formatTime(selectedSlice)})`}
                    size="small"
                    onClick={handleDeleteSelected}
                    sx={{
                      height: 22, fontSize: '0.65rem', fontWeight: 600, cursor: 'pointer',
                      bgcolor: '#374151', color: '#f87171', border: '1px solid #4b5563',
                      '&:hover': { bgcolor: '#4b5563' },
                    }}
                  />
                )}
                <Chip
                  label="📥 Export"
                  size="small"
                  onClick={handleExportGroundTruth}
                  disabled={slicePoints.length === 0}
                  sx={{
                    height: 22, fontSize: '0.65rem', fontWeight: 600, cursor: 'pointer',
                    bgcolor: '#374151', color: '#10b981', border: '1px solid #4b5563',
                    '&:hover': { bgcolor: '#4b5563' },
                    opacity: slicePoints.length === 0 ? 0.4 : 1,
                  }}
                />
                <Typography variant="caption" sx={{ color: '#4b5563', ml: 0.5 }}>
                  {slicePoints.length + 1} segment{slicePoints.length !== 0 ? 's' : ''}
                </Typography>
              </Box>
            </Box>

            {selectedSlice !== null && (
              <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: '#a78bfa', fontSize: '0.65rem' }}>
                  Selected: {formatTime(selectedSlice)} · Arrow keys to nudge (Shift for larger) · Del to remove · Esc to deselect · Drag to move
                </Typography>
              </Box>
            )}

            {/* Ground Truth Bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#a78bfa', flexShrink: 0 }} />
              <Typography variant="caption" sx={{ color: '#a78bfa', fontWeight: 600, width: 70, flexShrink: 0 }}>
                GT
              </Typography>
              <Box
                ref={groundTruthBarRef}
                onClick={handleGroundTruthClick}
                sx={{ 
                  flex: 1, height: 36, bgcolor: '#374151', borderRadius: 1,
                  position: 'relative', overflow: 'visible', cursor: 'crosshair', userSelect: 'none',
                }}
              >
                {slicePoints.map((point) => {
                  const isSelected = selectedSlice === point;
                  return (
                    <Tooltip key={`slice-${point}`} title={`${formatTime(point)} · Click to select · Drag to move`}>
                      <Box
                        onMouseDown={(e) => handleDragStart(point, e)}
                        onClick={(e) => handleSelectSlice(point, e)}
                        sx={{
                          position: 'absolute', left: `${(point / sessionDuration) * 100}%`,
                          top: -4, bottom: -4, width: isSelected ? 5 : 3,
                          bgcolor: isSelected ? '#a78bfa' : '#e5e7eb',
                          cursor: isDragging ? 'grabbing' : 'grab',
                          zIndex: isSelected ? 8 : 5, borderRadius: 0.5,
                          transition: isDragging ? 'none' : 'background-color 0.15s, width 0.15s, box-shadow 0.15s',
                          boxShadow: isSelected ? '0 0 8px rgba(167, 139, 250, 0.6)' : 'none',
                          '&:hover': { bgcolor: isSelected ? '#a78bfa' : '#c4b5fd', width: 5 },
                        }}
                      />
                    </Tooltip>
                  );
                })}
                <Box sx={{
                  position: 'absolute', left: `${(currentTime / sessionDuration) * 100}%`,
                  top: 0, bottom: 0, width: 2, bgcolor: '#ffffff',
                  boxShadow: '0 0 4px rgba(255,255,255,0.5)', zIndex: 10,
                  pointerEvents: 'none', transition: isDragging ? 'none' : 'left 0.1s linear'
                }} />
              </Box>
              <Typography variant="caption" sx={{ color: '#6b7280', minWidth: 24, textAlign: 'right' }}>
                {slicePoints.length}
              </Typography>
            </Box>

            {/* Divider */}
            <Box sx={{ borderBottom: '1px solid #374151', mb: 1.5 }} />

            {/* Auto-Segmented Bar - Collapsible */}
            <Box 
              onClick={() => setAutoSegExpanded(prev => !prev)}
              sx={{ 
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
                mb: autoSegExpanded ? 1.5 : 1.5, cursor: 'pointer', '&:hover': { opacity: 0.8 } 
              }}
            >
              <Typography variant="caption" fontWeight="600" sx={{ color: '#6b7280', textTransform: 'uppercase' }}>
                Auto-Segmented
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label="📥 Export"
                  size="small"
                  onClick={(e) => { e.stopPropagation(); handleExportAutoSegments(); }}
                  disabled={autoSegments.length === 0}
                  sx={{
                    height: 22, fontSize: '0.65rem', fontWeight: 600, cursor: 'pointer',
                    bgcolor: '#374151', color: '#60a5fa', border: '1px solid #4b5563',
                    '&:hover': { bgcolor: '#4b5563' },
                    opacity: autoSegments.length === 0 ? 0.4 : 1,
                  }}
                />
                <Typography variant="caption" sx={{ color: '#4b5563' }}>
                  {autoSegments.length} segment{autoSegments.length !== 1 ? 's' : ''}
                  {terminalRunMarkers.length > 0 && ` · ${terminalRunMarkers.length} runs`}
                </Typography>
                <ExpandMore sx={{ 
                  fontSize: 16, color: '#6b7280', 
                  transform: autoSegExpanded ? 'rotate(180deg)' : 'rotate(0deg)', 
                  transition: 'transform 0.2s' 
                }} />
              </Box>
            </Box>

            {autoSegExpanded && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#60a5fa', flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: '#60a5fa', fontWeight: 600, width: 70, flexShrink: 0 }}>
                  Auto
                </Typography>
                <Box sx={{ 
                  flex: 1, height: 24, bgcolor: '#374151', borderRadius: 1,
                  position: 'relative', overflow: 'visible',
                }}>
                  {/* Segment blocks */}
                  {autoSegments.map((seg, index) => {
                    const segBehavior = seg.suggestedBehavior || seg.behavior;
                    const color = segBehavior?.color || '#4b5563';
                    const label = segBehavior?.label || 'Unknown';
                    const left = (seg.startTime / sessionDuration) * 100;
                    const width = ((seg.endTime - seg.startTime) / sessionDuration) * 100;

                    return (
                      <Tooltip 
                        key={seg.id || index}
                        title={`${seg.suggestedThinkingSubcategory ? seg.suggestedThinkingSubcategory : label}: ${formatTime(seg.startTime)} - ${formatTime(seg.endTime)}${seg.preQueryPause ? ` (${(seg.preQueryPause/1000).toFixed(1)}s pre-query pause)` : ''}`}
                      >
                        <Box
                          onClick={() => {
                            if (isTelemetry && playback.seek) playback.seek(seg.startTime);
                          }}
                          sx={{
                            position: 'absolute', left: `${left}%`,
                            width: `${Math.max(width, 0.3)}%`, height: '100%',
                            bgcolor: color, cursor: 'pointer',
                            borderRight: index < autoSegments.length - 1 ? '1px solid #1f2937' : 'none',
                            '&:hover': { opacity: 0.8 },
                          }}
                        />
                      </Tooltip>
                    );
                  })}

                  {/* TERMINAL_RUN emoji markers */}
                  {terminalRunMarkers.map((time, index) => (
                    <Tooltip key={`run-${index}`} title={`Test run at ${formatTime(time)}`}>
                      <Box
                        onClick={() => {
                          if (isTelemetry && playback.seek) playback.seek(time);
                        }}
                        sx={{
                          position: 'absolute',
                          left: `${(time / sessionDuration) * 100}%`,
                          top: '25%',
                          transform: 'translateX(-50%)',
                          fontSize: '10px',
                          lineHeight: 1,
                          cursor: 'pointer',
                          zIndex: 12,
                          userSelect: 'none',
                          filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.8))',
                        }}
                      >
                        ▶
                      </Box>
                    </Tooltip>
                  ))}

                  {/* Playhead */}
                  <Box sx={{
                    position: 'absolute', left: `${(currentTime / sessionDuration) * 100}%`,
                    top: 0, bottom: 0, width: 2, bgcolor: '#ffffff',
                    boxShadow: '0 0 4px rgba(255,255,255,0.5)', zIndex: 10,
                    pointerEvents: 'none', transition: 'left 0.1s linear'
                  }} />
                </Box>
                <Typography variant="caption" sx={{ color: '#6b7280', minWidth: 24, textAlign: 'right' }}>
                  {autoSegments.length}
                </Typography>
              </Box>
            )}

            {autoSegExpanded && <Box sx={{ borderBottom: '1px solid #374151', mb: 1.5 }} />}

            {/* Behavioral Labels Comparison */}
            <Box 
              onClick={() => setLabelsExpanded(prev => !prev)}
              sx={{ 
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
                mb: labelsExpanded ? 1.5 : 0, cursor: 'pointer', '&:hover': { opacity: 0.8 } 
              }}
            >
              <Typography variant="caption" fontWeight="600" sx={{ color: '#6b7280', textTransform: 'uppercase' }}>
                Behavioral Labels Comparison
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label="📥 Export"
                  size="small"
                  onClick={(e) => { e.stopPropagation(); handleExportLabelerLabels(); }}
                  disabled={sortedLabelers.length === 0}
                  sx={{
                    height: 22, fontSize: '0.65rem', fontWeight: 600, cursor: 'pointer',
                    bgcolor: '#374151', color: '#f59e0b', border: '1px solid #4b5563',
                    '&:hover': { bgcolor: '#4b5563' },
                    opacity: sortedLabelers.length === 0 ? 0.4 : 1,
                  }}
                />
                <ExpandMore sx={{ 
                  fontSize: 16, color: '#6b7280', 
                  transform: labelsExpanded ? 'rotate(180deg)' : 'rotate(0deg)', 
                  transition: 'transform 0.2s' 
                }} />
              </Box>
            </Box>
            
            {labelsExpanded && (
              <>
                {sortedLabelers.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, position: 'relative' }}>
                    {sortedLabelers.map((labeler) => (
                      <Box key={labeler.labelerName} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getLabelerColor(labeler.labelerName), flexShrink: 0 }} />
                        <Typography 
                          variant="caption" 
                          sx={{ color: '#9ca3af', fontWeight: 500, width: 70, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={labeler.labelerName}
                        >
                          {labeler.labelerName}
                        </Typography>
                        <Box sx={{ flex: 1, height: 20, bgcolor: '#374151', borderRadius: 1, position: 'relative', overflow: 'hidden' }}>
                          {labeler.tags?.map((tag, index) => {
                            const startTime = tag.startTime ?? tag.start_time ?? 0;
                            const endTime = tag.endTime ?? tag.end_time ?? 0;
                            const behaviorLabel = tag.behavior?.label ?? tag.behaviorLabel ?? tag.behavior_label ?? 'Unknown';
                            const behaviorColor = tag.behavior?.color ?? tag.behaviorColor ?? tag.behavior_color ?? '#6b7280';
                            const left = (startTime / sessionDuration) * 100;
                            const width = ((endTime - startTime) / sessionDuration) * 100;
                            
                            return (
                                <Tooltip key={index} title={(() => {
                                  const subcategory = tag.thinkingSubcategory ?? tag.thinking_subcategory;
                                  const base = `${behaviorLabel}: ${formatTime(startTime)} - ${formatTime(endTime)}`;
                                  return subcategory ? `${base} · ${subcategory}` : base;
                                })()}>                                <Box sx={{
                                  position: 'absolute', left: `${left}%`, width: `${Math.max(width, 0.5)}%`,
                                  height: '100%', bgcolor: behaviorColor, cursor: 'pointer', '&:hover': { opacity: 0.8 }
                                }} />
                              </Tooltip>
                            );
                          })}
                          {/* TERMINAL_RUN markers */}
                          {terminalRunMarkers.map((time, index) => (
                            <Tooltip key={`run-${index}`} title={`Test run at ${formatTime(time)}`}>
                              <Box
                                onClick={() => {
                                  if (isTelemetry && playback.seek) playback.seek(time);
                                }}
                                sx={{
                                  position: 'absolute',
                                  left: `${(time / sessionDuration) * 100}%`,
                                  top: '25%',
                                  transform: 'translateX(-50%)',
                                  fontSize: '10px',
                                  lineHeight: 1,
                                  cursor: 'pointer',
                                  zIndex: 12,
                                  userSelect: 'none',
                                  filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.8))',
                                }}
                              >
                                ▶
                              </Box>
                            </Tooltip>
                          ))}
                          <Box sx={{
                            position: 'absolute', left: `${(currentTime / sessionDuration) * 100}%`,
                            top: 0, bottom: 0, width: 2, bgcolor: '#ffffff',
                            boxShadow: '0 0 4px rgba(255,255,255,0.5)', zIndex: 10,
                            pointerEvents: 'none', transition: 'left 0.1s linear'
                          }} />
                        </Box>
                        <Typography variant="caption" sx={{ color: '#6b7280', minWidth: 24, textAlign: 'right' }}>
                          {labeler.tags?.length || 0}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>No labeling data available</Typography>
                )}
                
                <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>
                    Current: {formatTime(currentTime)}
                  </Typography>
                  {behavioralIRR !== null && (
                    <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                      IRR: <span style={{ color: '#e5e7eb', fontWeight: 600 }}>{behavioralIRR}%</span>
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* Right: Comparison Sidebar - Collapsible */}
        {sidebarOpen ? (
          <Box sx={{ width: 380, bgcolor: '#ffffff', borderLeft: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #e5e7eb', minHeight: 42 }}>
              <Tabs 
                value={activeTab} 
                onChange={(e, v) => setActiveTab(v)} 
                sx={{ flex: 1, minHeight: 42 }}
                variant="fullWidth"
              >
                <Tab label="Pre-Query Description" sx={{ minWidth: 0, flex: 1, fontSize: '0.75rem', textTransform: 'none' }} />
                <Tab label="Pre-Query Productivity" sx={{ minWidth: 0, flex: 1, fontSize: '0.75rem', textTransform: 'none' }} />
                <Tab label="Pre-Query Intervention" sx={{ minWidth: 0, flex: 1, fontSize: '0.75rem', textTransform: 'none' }} />
              </Tabs>
              <IconButton 
                onClick={() => setSidebarOpen(false)} 
                sx={{ color: '#9ca3af', mx: 0.5, '&:hover': { bgcolor: '#f3f4f6' } }}
                size="small"
              >
                <ChevronRight sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
            
            <Box sx={{ flex: 1, p: 1.5, overflowY: 'auto' }}>
              {activeTab === 0 && renderDescriptionTab()}
              {activeTab === 1 && renderProductivityTab()}
              {activeTab === 2 && renderInterventionTab()}
            </Box>
          </Box>
        ) : (
          <Box 
            onClick={() => setSidebarOpen(true)}
            sx={{ width: 28, bgcolor: '#ffffff', borderLeft: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, '&:hover': { bgcolor: '#f3f4f6' } }}
          >
            <ChevronLeft sx={{ fontSize: 18, color: '#9ca3af' }} />
          </Box>
        )}
      </Box>
    </Box>
  );
}